import { useEffect, useRef, useCallback } from "react";
import { hasAnalyticsConsent } from "@/components/CookieConsentBanner";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

interface ScrollDepthConfig {
  thresholds?: number[]; // Percentages to track (default: 25, 50, 75, 90, 100)
  contentSlug: string;
  contentTitle: string;
}

export const useScrollDepthTracking = ({
  thresholds = [25, 50, 75, 90, 100],
  contentSlug,
  contentTitle,
}: ScrollDepthConfig) => {
  const startTime = useRef<number>(Date.now());
  const maxScrollDepth = useRef<number>(0);
  const trackedThresholds = useRef<Set<number>>(new Set());
  const hasTrackedTimeOnPage = useRef<boolean>(false);

  const trackEvent = useCallback(
    (action: string, params: Record<string, unknown>) => {
      if (!hasAnalyticsConsent()) return;
      
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", action, {
          event_category: "content_engagement",
          content_slug: contentSlug,
          content_title: contentTitle,
          ...params,
        });
      }
    },
    [contentSlug, contentTitle]
  );

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      
      // Calculate scroll percentage
      const scrollableHeight = documentHeight - windowHeight;
      if (scrollableHeight <= 0) return;
      
      const scrollPercentage = Math.round((scrollTop / scrollableHeight) * 100);
      
      // Update max scroll depth
      if (scrollPercentage > maxScrollDepth.current) {
        maxScrollDepth.current = scrollPercentage;
      }

      // Track threshold milestones
      thresholds.forEach((threshold) => {
        if (
          scrollPercentage >= threshold &&
          !trackedThresholds.current.has(threshold)
        ) {
          trackedThresholds.current.add(threshold);
          trackEvent("scroll_depth", {
            scroll_depth: threshold,
            time_on_page_seconds: Math.round((Date.now() - startTime.current) / 1000),
          });
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [thresholds, trackEvent]);

  // Track time on page when leaving
  useEffect(() => {
    const trackTimeOnPage = () => {
      if (hasTrackedTimeOnPage.current) return;
      hasTrackedTimeOnPage.current = true;
      
      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      
      // Categorize engagement
      let engagementLevel = "bounce";
      if (timeOnPage >= 180 && maxScrollDepth.current >= 75) {
        engagementLevel = "high";
      } else if (timeOnPage >= 60 && maxScrollDepth.current >= 50) {
        engagementLevel = "medium";
      } else if (timeOnPage >= 15 || maxScrollDepth.current >= 25) {
        engagementLevel = "low";
      }

      trackEvent("page_engagement", {
        time_on_page_seconds: timeOnPage,
        max_scroll_depth: maxScrollDepth.current,
        engagement_level: engagementLevel,
      });
    };

    // Track on visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        trackTimeOnPage();
      }
    };

    // Track on page unload
    const handleBeforeUnload = () => {
      trackTimeOnPage();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      trackTimeOnPage();
    };
  }, [trackEvent]);

  return {
    maxScrollDepth: maxScrollDepth.current,
    timeOnPage: Math.round((Date.now() - startTime.current) / 1000),
  };
};
